import { useState } from "react";
import { Page, useCurrent, Grid } from "./common";
import { PageHead, Avatar } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money } from "../lib/store";
import { placementOf } from "../lib/portalNav";
import { useCan } from "../lib/auth";
import { BUILDING_TYPES, isInterior, isStacked } from "../lib/program";
import { fmtDate } from "../lib/format";
import { InviteClient } from "../components/InviteClient";

/* The client-portal features the studio can turn on/off. "Overview" is always
   on (it's the portal home). Order mirrors the portal's own tab order. */
const PORTAL_FEATURES: { key: string; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "brief", label: "Brief" }, { key: "moodboard", label: "Mood board" }, { key: "direction", label: "Direction" }, { key: "concept", label: "The concept" }, { key: "film", label: "Film" },
  { key: "fees", label: "Fees & scope" }, { key: "configure", label: "Configure scope" }, { key: "engagement", label: "Engagement" },
  { key: "designs", label: "Designs" }, { key: "drawings", label: "Drawings" }, { key: "packages", label: "Packages" }, { key: "tender", label: "Tendering" },
  { key: "budget", label: "Budget & size" }, { key: "story", label: "The story" }, { key: "spaces", label: "Your spaces" }, { key: "approvals", label: "Approvals" }, { key: "reviews", label: "Reviews" }, { key: "rfis", label: "RFIs" }, { key: "progress", label: "Progress" }, { key: "consultants", label: "Consultants" },
  { key: "reports", label: "Reports" }, { key: "billing", label: "Billing" }, { key: "messages", label: "Conversation" },
];

export default function ProjectData() {
  const p = useCurrent();
  const canEdit = useCan("brief");
  const [copied, setCopied] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  if (!p) return null;
  const upd = (patch: any) => actions.updateProject(p.id, patch);
  const updC = (patch: any) => actions.updateClient(p.id, patch);
  const ro = !canEdit;
  const types = p.types ?? (p.type ? [p.type] : []);

  return (
    <Page max={960}>
      <PageHead kicker={`${p.name} · ${p.code}`} title="Project data" />
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 22 }}>
        The core facts of the project. Fill these in first — the AI brief, space program and fee estimate all read from here.
      </p>

      <Grid cols="1.3fr 1fr" gap={20}>
        <div className="col" style={{ gap: 20 }}>
          {/* project */}
          <div className="card pad reveal">
            <div className="kicker" style={{ marginBottom: 16 }}>Project</div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div className="field" style={{ gridColumn: "1 / -1" }}><label>Project name</label><input className="input" disabled={ro} value={p.name} onChange={(e) => upd({ name: e.target.value })} /></div>

              {/* multi building types */}
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label>Building type(s) <span className="dim">— select more than one for mixed-use</span></label>
                <div className="row wrap" style={{ gap: 7, marginTop: 4 }}>
                  {BUILDING_TYPES.map((t) => {
                    const on = types.includes(t);
                    return (
                      <button key={t} type="button" disabled={ro} className="chip" style={{ fontSize: 12, background: on ? "var(--ink)" : "var(--card)", color: on ? "var(--paper)" : "var(--ink-2)", borderColor: on ? "var(--ink)" : "var(--line-2)", cursor: ro ? "default" : "pointer" }}
                        onClick={() => { const next = on ? types.filter((x) => x !== t) : [...types, t]; actions.setProjectTypes(p.id, next); }}>
                        {on && <Icon.check width={12} />}{t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* overall area */}
              <div className="field" style={{ gridColumn: "1 / -1" }}><label>Overall area <span className="dim">(GIA — anchors the AI schedule)</span></label>
                <div className="row" style={{ gap: 8 }}>
                  <input className="input" type="number" disabled={ro} value={p.gia || ""} onChange={(e) => actions.setGia(p.id, Number(e.target.value), p.giaUnit ?? "sqft")} placeholder="e.g. 50000" style={{ maxWidth: 200 }} />
                  <select className="select" style={{ width: 80 }} disabled={ro} value={p.giaUnit ?? "sqft"} onChange={(e) => actions.setGia(p.id, p.gia ?? 0, e.target.value as any)}>
                    <option value="sqft">ft²</option><option value="sqm">m²</option>
                  </select>
                  {!p.gia && p.area && <span className="dim" style={{ fontSize: 12.5 }}>currently: {p.area}</span>}
                </div>
              </div>

              {/* floors — drives stacked (multi-family) programmes */}
              {types.some(isStacked) && (
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>Typical floors <span className="dim">— residential storeys; multiplies the per-floor accommodation</span></label>
                  <div className="row" style={{ gap: 10, alignItems: "center" }}>
                    <input className="input" type="number" min={1} max={80} disabled={ro} value={p.floors ?? 1}
                      onChange={(e) => actions.setFloors(p.id, Number(e.target.value))} style={{ maxWidth: 120 }} />
                    <span className="dim" style={{ fontSize: 12.5 }}>
                      Regenerate the space program from the Brief to apply — apartments and cores repeat per floor; the lobby, plant and parking stay shared.
                    </span>
                  </div>
                </div>
              )}

              <div className="field"><label>Location</label><input className="input" disabled={ro} value={p.location} onChange={(e) => upd({ location: e.target.value })} placeholder="City, Country" /></div>
              <div className="field"><label>Survey plan</label>
                <select className="select" disabled={ro} value={p.surveyPlan ?? "unknown"} onChange={(e) => upd({ surveyPlan: e.target.value })}>
                  <option value="yes">Available</option><option value="no">Not available</option><option value="unknown">To confirm</option>
                </select>
                {p.surveyDoc && (
                  <a className="row" href={p.surveyDoc.url} target="_blank" rel="noreferrer" style={{ gap: 6, fontSize: 12, color: "var(--accent-ink)", marginTop: 6 }}>
                    <Icon.checkCircle width={13} style={{ color: "var(--ok)" }} />Client provided: {p.surveyDoc.name || "survey"} <Icon.arrowUpRight width={12} />
                  </a>
                )}
              </div>
              <div className="field" style={{ gridColumn: "1 / -1" }}><label>Construction budget</label>
                <div className="row" style={{ gap: 8 }}>
                  <select className="select" style={{ width: 92 }} disabled={ro} value={p.currency} onChange={(e) => upd({ currency: e.target.value })}>{["USD", "EUR", "GBP", "GYD"].map((c) => <option key={c}>{c}</option>)}</select>
                  <input className="input" type="number" disabled={ro} value={p.budget || ""} onChange={(e) => upd({ budget: Number(e.target.value) })} />
                  <span className="dim" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{money(p.budget, p.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* location pin */}
          <div className="card pad reveal">
            <div className="row between" style={{ marginBottom: 14 }}>
              <div className="kicker">Site location</div>
              {canEdit && <button className="btn ghost sm" onClick={() => navigator.geolocation?.getCurrentPosition((pos) => upd({ geo: { lat: +pos.coords.latitude.toFixed(4), lng: +pos.coords.longitude.toFixed(4) } }))}><Icon.pin width={14} />Use my location</button>}
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div className="field"><label>Latitude</label><input className="input" disabled={ro} value={p.geo?.lat ?? ""} onChange={(e) => upd({ geo: { lat: Number(e.target.value), lng: p.geo?.lng ?? 0 } })} placeholder="6.8013" /></div>
              <div className="field"><label>Longitude</label><input className="input" disabled={ro} value={p.geo?.lng ?? ""} onChange={(e) => upd({ geo: { lat: p.geo?.lat ?? 0, lng: Number(e.target.value) } })} placeholder="-58.1551" /></div>
            </div>
            {p.geo && (p.geo.lat || p.geo.lng) && (
              <div style={{ marginTop: 14, borderRadius: 12, overflow: "hidden", border: "1px solid var(--line)" }}>
                <iframe title="Location" width="100%" height="220" style={{ border: 0, display: "block" }} loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${p.geo.lng - 0.02}%2C${p.geo.lat - 0.012}%2C${p.geo.lng + 0.02}%2C${p.geo.lat + 0.012}&layer=mapnik&marker=${p.geo.lat}%2C${p.geo.lng}`} />
              </div>
            )}
          </div>
        </div>

        {/* client */}
        <div className="col" style={{ gap: 20 }}>
          <div className="card pad reveal">
            <div className="kicker" style={{ marginBottom: 14 }}>Client</div>
            <div className="row" style={{ gap: 12, marginBottom: 16 }}>
              <Avatar name={p.client.name} hue={p.client.avatarHue} size={44} />
              <div><div style={{ fontWeight: 600 }}>{p.client.name}</div><div className="dim" style={{ fontSize: 12.5 }}>{p.client.company}</div></div>
            </div>
            <div className="col" style={{ gap: 12 }}>
              <div className="field"><label>Contact name</label><input className="input" disabled={ro} value={p.client.name} onChange={(e) => updC({ name: e.target.value })} /></div>
              <div className="field"><label>Company</label><input className="input" disabled={ro} value={p.client.company} onChange={(e) => updC({ company: e.target.value })} /></div>
              <div className="field"><label>Email</label><input className="input" disabled={ro} value={p.client.email} onChange={(e) => updC({ email: e.target.value })} /></div>
              <div className="field"><label>Phone</label><input className="input" disabled={ro} value={p.client.phone ?? ""} onChange={(e) => updC({ phone: e.target.value })} placeholder="+…" /></div>
            </div>

            {/* additional people on the client side */}
            <ClientContacts p={p} ro={ro} />

            <div className="divider" style={{ margin: "16px 0" }} />
            <div className="kicker" style={{ marginBottom: 8 }}>Client portal access</div>
            <div className="panel row between" style={{ padding: "10px 12px" }}>
              <span className="mono" style={{ fontSize: 13 }}>{copied ? "copied ✓" : p.client.code}</span>
              <div className="row" style={{ gap: 6 }}>
                <button className="iconbtn" style={{ width: 30, height: 30 }} title="Copy code" onClick={() => { navigator.clipboard?.writeText(p.client.code); setCopied(true); setTimeout(() => setCopied(false), 1500); }}><Icon.brief width={14} /></button>
                {canEdit && <button className="iconbtn" style={{ width: 30, height: 30 }} title="Reissue code" onClick={() => actions.reissueClientCode(p.id)}><Icon.swap width={14} /></button>}
              </div>
            </div>
            <button className="btn soft sm" style={{ marginTop: 10, width: "100%" }} onClick={() => setInviteOpen(true)}><Icon.send width={14} />Invite {p.client.name.split(" ")[0]}</button>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>Share this privately — the client signs in via the Client tab.</div>
          </div>

          <div className="card pad reveal">
            <div className="kicker" style={{ marginBottom: 10 }}>Summary</div>
            <div className="col" style={{ gap: 8, fontSize: 13 }}>
              <div className="row between"><span className="dim">Code</span><span className="mono">{p.code}</span></div>
              <div className="row between"><span className="dim">Type</span><span>{p.type}{isInterior(p.type) ? " · interiors" : ""}</span></div>
              <div className="row between"><span className="dim">Appointed</span><span>{fmtDate(p.createdOn)}</span></div>
              <div className="row between"><span className="dim">Budget</span><span>{money(p.budget, p.currency)}</span></div>
            </div>
          </div>
        </div>
      </Grid>

      {/* What the client sees — and where it sits. Three states, because "off"
          and "not in the way" are different decisions. */}
      <div className="card pad reveal" style={{ marginTop: 20 }}>
        <div className="row between wrap" style={{ gap: 10, marginBottom: 4 }}>
          <div className="kicker">What the client sees</div>
          <span className="dim" style={{ fontSize: 11.5 }}>
            {PORTAL_FEATURES.filter((f) => placementOf(p, f.key) === "front").length} up front ·
            {" "}{PORTAL_FEATURES.filter((f) => placementOf(p, f.key) === "menu").length} in the menu ·
            {" "}{(p.portalHidden ?? []).length} off
          </span>
        </div>
        <p className="muted" style={{ fontSize: 12.5, marginBottom: 14, maxWidth: 700 }}>
          Curate {p.client.name.split(" ")[0]}'s portal. <b>Up front</b> sits in the top row; <b>In the menu</b> stays one
          click away without competing with live work; <b>Off</b> hides it entirely. Left on <b>Auto</b>, the project
          decides — whatever is waiting on them comes forward, and finished business (a signed engagement, an approved
          brief) moves into the menu on its own.
        </p>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 8 }}>
          {PORTAL_FEATURES.map((f) => {
            const explicit = (p.portalPinned ?? []).includes(f.key) ? "front"
              : (p.portalTucked ?? []).includes(f.key) ? "menu"
              : (p.portalHidden ?? []).includes(f.key) ? "off" : "auto";
            const actual = placementOf(p, f.key);
            const always = f.key === "overview" || f.key === "messages";
            return (
              <div key={f.key} className="panel row between wrap" style={{ padding: "8px 10px", gap: 8 }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{f.label}</span>
                  {explicit === "auto" && (
                    <span className="dim" style={{ display: "block", fontSize: 10.5, marginTop: 1 }}>
                      auto → {actual === "front" ? "up front" : actual === "menu" ? "in the menu" : "off"}
                    </span>
                  )}
                </span>
                <span className="row" style={{ gap: 3, flexShrink: 0 }}>
                  {([["auto", "Auto"], ["front", "Front"], ["menu", "Menu"], ["off", "Off"]] as const).map(([k, lab]) => (
                    <button key={k} disabled={ro || (always && k === "off")}
                      onClick={() => actions.setPortalPlacement(p.id, f.key, k)}
                      title={always && k === "off" ? `${f.label} is always shown` : undefined}
                      style={{
                        fontSize: 10.5, padding: "3px 7px", borderRadius: 5, cursor: ro ? "default" : "pointer",
                        border: "1px solid " + (explicit === k ? "var(--accent)" : "var(--line-2)"),
                        background: explicit === k ? "var(--accent)" : "var(--card)",
                        color: explicit === k ? "#fff" : "var(--ink-3)",
                        opacity: always && k === "off" ? 0.35 : 1,
                      }}>{lab}</button>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
        {canEdit && ((p.portalHidden ?? []).length > 0 || (p.portalPinned ?? []).length > 0 || (p.portalTucked ?? []).length > 0) && (
          <button className="btn ghost sm" style={{ marginTop: 12 }} onClick={() => actions.resetPortalCuration(p.id)}>
            <Icon.swap width={13} />Back to auto for everything
          </button>
        )}
      </div>

      <InviteClient p={p} open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </Page>
  );
}

/* Additional client-side people beyond the primary contact — a finance lead, a
   spouse, a facilities manager. Marking one "primary" promotes them to the main
   contact everything else reads (invites, portal greeting, WhatsApp). */
function ClientContacts({ p, ro }: { p: any; ro: boolean }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", role: "", email: "", phone: "" });
  const contacts = (p.client.contacts ?? []) as { id: string; name: string; role?: string; email?: string; phone?: string; primary?: boolean }[];
  function add() {
    if (!f.name.trim()) return;
    actions.addClientContact(p.id, { name: f.name, role: f.role, email: f.email, phone: f.phone });
    setF({ name: "", role: "", email: "", phone: "" }); setAdding(false);
  }
  return (
    <div style={{ marginTop: 16 }}>
      <div className="row between" style={{ marginBottom: 8 }}>
        <span className="kicker">Other contacts &amp; representatives</span>
        {!ro && !adding && <button className="chip" style={{ fontSize: 11 }} onClick={() => setAdding(true)}><Icon.plus width={12} />Add</button>}
      </div>
      {contacts.length === 0 && !adding && <div className="dim" style={{ fontSize: 12.5 }}>Just the primary contact so far.</div>}
      <div className="col" style={{ gap: 8 }}>
        {contacts.map((c) => (
          <div key={c.id} className="panel" style={{ padding: "10px 12px" }}>
            <div className="row between" style={{ gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>{c.name}</span>
                  {c.primary
                    ? <span className="badge ok" style={{ fontSize: 8.5 }}>Primary</span>
                    : c.role ? <span className="badge" style={{ fontSize: 8.5 }}>{c.role}</span> : null}
                </div>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>
                  {[c.primary && c.role, c.email, c.phone].filter(Boolean).join(" · ") || "No contact details"}
                </div>
              </div>
              {!ro && (
                <div className="row" style={{ gap: 4, flexShrink: 0 }}>
                  {!c.primary && <button className="iconbtn" style={{ width: 28, height: 28 }} title="Make primary contact" onClick={() => actions.updateClientContact(p.id, c.id, { primary: true })}><Icon.check width={13} /></button>}
                  <button className="iconbtn" style={{ width: 28, height: 28 }} title="Remove" onClick={() => actions.removeClientContact(p.id, c.id)}><Icon.close width={13} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {adding && (
        <div className="panel" style={{ padding: 12, marginTop: 8 }}>
          <div className="row" style={{ gap: 8, marginBottom: 8 }}>
            <input className="input" autoFocus placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <input className="input" placeholder="Role — e.g. Finance" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
          </div>
          <div className="row" style={{ gap: 8, marginBottom: 10 }}>
            <input className="input" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <input className="input" placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>
          <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
            <button className="btn ghost sm" onClick={() => { setAdding(false); setF({ name: "", role: "", email: "", phone: "" }); }}>Cancel</button>
            <button className="btn accent sm" disabled={!f.name.trim()} onClick={add}><Icon.check width={14} />Add contact</button>
          </div>
        </div>
      )}
    </div>
  );
}
